import cx from 'clsx';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React, { useRef, useState } from 'react';

import { ReactComponent as ChartArrowIcon } from '@assets/svg/ic-chevron.svg';
import { ReactComponent as RefetchIcon } from '@assets/svg/ic-return.svg';
import { ReactComponent as ThreeDotsMoreIcon } from '@assets/svg/three-dots.svg';

import SkeletonBlock from '@components/common/Skeleton/SkeletonBlock';
import Tabs from '@components/uikit/Tabs/Tabs';

import useTranslation from '@hooks/useTranslation';

import type { IChartType } from '@model/chartType';
import type { IInstallationPoint } from '@model/installationPoint';

import { selectAppPersistDarkMode } from '@store/useAppStorePersist';

import { CHART_TIMES } from '../../constants/chartTimes';
import { getChartPrintTitle } from '../../helpers/getChartPrintTitle';
import { getChartRmsOptions } from '../../helpers/getChartRmsOptions';
import { useChartAlertConditionsQuery } from '../../services/useChartAlertConditionsQuery';
import { useChartQuery } from '../../services/useChartQuery';
import DownloadContextMenu from '../DownloadContextMenu/DownloadContextMenu';
import style from './style.module.scss';

interface IProps {
  point: IInstallationPoint;
  chartType: IChartType;
  timeValue: number;
  dateTimeFormat: string;
  alertTooltipId: string;
}

const ChartRmsView: React.FC<IProps> = ({ point, chartType, timeValue, dateTimeFormat, alertTooltipId }) => {
  const { t } = useTranslation();

  const darkMode = selectAppPersistDarkMode();

  const [readingTypeId, setReadingTypeId] = useState<number>(chartType?.series?.[0]?.readingType);
  const [isOpenChart, setIsOpenChart] = useState<boolean>(true);
  const [maxChartValue, setMaxChartValue] = useState<number | null>(null);

  const printRef = useRef<HTMLDivElement | null>(null);
  const chartComponentRef = useRef(null);

  const axisName =
    chartType.alias === 'temp'
      ? ''
      : chartType.series.find((axis) => axis.readingType === readingTypeId)?.axisName || '';
  const showAxes = chartType.alias !== 'temp' && chartType.alias !== 'wua' && chartType.alias !== 'boolean';

  const {
    isFetching: isChartFetching,
    data: chartDataQuery,
    refetch: refetchCharts,
  } = useChartQuery({
    pointId: point.id,
    readingTypeId,
    time: timeValue,
    pointType: point.point_type,
  });

  const { data: alertConditionsData, refetch: refetchAlertConditions } = useChartAlertConditionsQuery({
    pointId: point.id,
    readingTypeId,
    alertTooltipId,
    unit: chartType.unit,
  });

  const chartOptions = getChartRmsOptions(
    t,
    chartDataQuery || [],
    point.point_type,
    dateTimeFormat,
    maxChartValue,
    alertConditionsData?.alertPlotLines,
    chartType.unit,
    darkMode,
  );

  const chartTitle = getChartPrintTitle(
    t,
    point,
    chartType,
    axisName,
    t(CHART_TIMES[timeValue as keyof typeof CHART_TIMES] || ''),
  );

  const getRefetchData = () => {
    refetchAlertConditions();
    refetchCharts();
    setMaxChartValue(0);
  };

  const onChartCallback = (chart: Highcharts.Chart) => {
    const maxYAxisChartValue = chart.yAxis[0].getExtremes().max;
    const maxAlertCondition: number | null = alertConditionsData?.maxAlertValue || null;

    if (maxAlertCondition !== null && maxYAxisChartValue < maxAlertCondition) {
      setMaxChartValue(maxAlertCondition);
    }
  };

  return (
    <div className={cx(style.chart, 'onPrintChart')}>
      <div className={cx(style.chartHead, 'onPrintHead')}>
        <div className={style.chartInfo} onClick={() => setIsOpenChart(!isOpenChart)}>
          <ChartArrowIcon
            className={cx(style.chartArrowIcon, { [style.chartArrowIconIsOpen]: isOpenChart })}
          />
          <span className={style.chartInfoTitle}>
            {t(chartType.label)} {!!chartType.unit && `(${chartType.unit})`}
          </span>
        </div>
        <div className={style.chartActions}>
          {!!point?.wosTrigger && (
            <div
              className={style.wos}
              data-tooltip-id="chart-info"
              data-tooltip-content={t(
                'Wake-on-Shake is enabled for this sensor, which will determine when vibration readings are collected',
              )}
            >
              WoS
            </div>
          )}
          <RefetchIcon
            className={style.refetchIcon}
            data-tooltip-id="chart-info"
            data-tooltip-content={t(`Refresh chart data`)}
            onClick={getRefetchData}
          />
          {showAxes && (
            <Tabs
              value={readingTypeId}
              tabsItems={chartType.series}
              label={t('Data axis')}
              type={'woHelperText'}
              onChange={(value: any) => {
                setReadingTypeId(value);
                setMaxChartValue(null);
              }}
              itemKeys={{ valueFieldKey: 'readingType', labelFieldKey: 'axisName' }}
            />
          )}
          <div
            className={style.downloadActions}
            data-tooltip-id={`download-${chartType.alias}-context-menu`}
            data-tooltip-content={JSON.stringify({
              timeValue,
              readingId: readingTypeId,
            })}
          >
            <ThreeDotsMoreIcon />
          </div>
        </div>
      </div>
      <div className={cx(style.chartView, 'chartViewOnPrint', { [style.chartViewOpen]: isOpenChart })}>
        {isChartFetching && <SkeletonBlock className={style.skeletonChart} />}
        {!isChartFetching && (
          <div className={cx(style.chartWrapper, 'chartWrapperOnPrint')} ref={printRef}>
            <div className={cx(style.printTitle, 'onPrintTitle')}>{chartTitle}</div>
            {!chartDataQuery?.length ? (
              <div className={style.noData}>{t('No Data')}</div>
            ) : (
              <HighchartsReact
                ref={chartComponentRef}
                highcharts={Highcharts}
                options={chartOptions}
                containerProps={{ className: 'printSensorHighchartsChart' }}
                callback={onChartCallback}
              />
            )}
          </div>
        )}
      </div>
      <DownloadContextMenu
        id={`download-${chartType.alias}-context-menu`}
        dateTimeFormat={dateTimeFormat}
        point={point}
        printRef={printRef}
        chartTitle={chartTitle}
        chartTypeLabel={chartType.label}
      />
    </div>
  );
};

export default ChartRmsView;
